'use client';

import { c as _c } from "react/compiler-runtime";
import type { SuggestedAction } from '@/data/chat/chat';
import { LocateFixedIcon, MessageCircleQuestionMarkIcon, RouteIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import { CHAT_SUGGESTION_ROW_LAYOUT_CLASS } from './chat-suggestions-skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
type ChatSuggestionRowKind = NonNullable<SuggestedAction['kind']> | 'tour';

// Tour gets the Route glyph; other navigation chips reuse the focusCode tool-header glyph and
// questions get a question bubble, so the chip icon teaches what clicking will do.
const SUGGESTION_ICONS = {
  tour: RouteIcon,
  navigate: LocateFixedIcon,
  question: MessageCircleQuestionMarkIcon
} satisfies Record<ChatSuggestionRowKind, unknown>;
type ChatSuggestionRowProps = Omit<ComponentProps<typeof Button>, 'children'> & {
  kind: ChatSuggestionRowKind;
  label: string;
};

/**
 * A suggested prompt in the chat column: an icon and a one-line title, boxed only on hover.
 * Render rows inside a CHAT_SUGGESTION_LIST_CLASS element so their icons line up with the text.
 */
export function ChatSuggestionRow(t0) {
  const $ = _c(16);
  let className;
  let kind;
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      kind,
      label,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = kind;
    $[3] = label;
    $[4] = props;
  } else {
    className = $[1];
    kind = $[2];
    label = $[3];
    props = $[4];
  }
  const Icon = SUGGESTION_ICONS[kind];
  let t1;
  if ($[5] !== className) {
    t1 = cn(CHAT_SUGGESTION_ROW_LAYOUT_CLASS, "group justify-start rounded-md text-left text-xs font-normal leading-5 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200 focus-visible:bg-zinc-900 focus-visible:text-zinc-200", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== Icon) {
    t2 = <Icon aria-hidden={true} className="size-3.5! shrink-0 text-zinc-500 group-hover:text-zinc-400 group-focus-visible:text-zinc-400" />;
    $[7] = Icon;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== label) {
    t3 = <span className="min-w-0 truncate">{label}</span>;
    $[9] = label;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== props || $[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <Button type="button" variant="ghost" className={t1} {...props}>{t2}{t3}</Button>;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}